import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Filter, Plus, User, ChevronRight, MoreVertical, Edit, UserPlus, Trash2, Eye, X } from 'lucide-react';
import { patients } from '../services/api';
import { isDemoMode, maskPatientName, getDemoModeIndicator, logDemoActivity } from '../utils/demoMode';
import { usePatientAccess } from '../utils/patientAccess';

const PatientList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterInsurance, setFilterInsurance] = useState('all');
  const [patientsList, setPatientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use patient access utility
  const { currentUser, hasAdminAccess, getUserAccessiblePatients } = usePatientAccess();

  const fetchPatients = async () => {
    try {
      setLoading(true);
      console.log('Fetching patients...');
      
      if (!currentUser) {
        console.error('No current user found');
        setPatientsList([]);
        return;
      }
      
      console.log('Current user role:', currentUser?.role);
      
      let patientsData;
      
      // Use the patient access utility to get accessible patients
      if (hasAdminAccess) {
        // Admins can see all patients
        console.log('Admin/Operations user - fetching all patients');
        const response = await patients.getAll();
        patientsData = response.data.data;
      } else {
        // Supervisor/therapist users - get their assigned patients
        console.log('Limited access user - fetching assigned patients only');
        const accessiblePatients = await getUserAccessiblePatients();
        patientsData = accessiblePatients === 'ALL_PATIENTS' ? [] : accessiblePatients;
        console.log('Assigned patients:', patientsData);
      }
      
      setPatientsList(patientsData);

      // Extract unique insurance companies from patients
      const uniqueInsuranceCompanies = Array.from(
        new Set(
          patientsData
            .filter(patient => patient.insuranceCompany)
            .map(patient => ({
              id: patient.insuranceCompany.id,
              name: patient.insuranceCompany.name
            }))
        )
      );
      setInsuranceCompanies(uniqueInsuranceCompanies);

    } catch (error) {
      console.error('Error fetching patients:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  // Refresh patients list when navigated with refresh flag
  useEffect(() => {
    if (location.state?.refresh) {
      fetchPatients();
      // Clear the refresh flag
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, navigate]);

  // Filter patients based on search term, status, and insurance
  const filteredPatients = patientsList.filter(patient => {
    const matchesSearch = 
      (patient.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.patientId || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || patient.status === filterStatus;
    const matchesInsurance = filterInsurance === 'all' || 
      (patient.insuranceCompany && patient.insuranceCompany.id === filterInsurance);
    return matchesSearch && matchesStatus && matchesInsurance;
  });

  const handleAddPatient = () => {
    navigate('/dashboard/patients/new');
  };

  const handleViewPatient = (patientId) => {
    if (!patientId) {
      console.error('No patient ID provided');
      return;
    }
    console.log('Navigating to patient:', patientId);
    navigate(`/dashboard/patients/${patientId}`);
  };

  const handleEditPatient = (patientId) => {
    navigate(`/dashboard/patients/${patientId}/edit`);
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleDeleteConfirm = () => {
    // Implement the delete logic here
    console.log('Deleting patient:', selectedPatient);
    setShowDeleteConfirm(false);
    setSuccess('Paciente excluído com sucesso!');
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0 mb-6">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Pacientes</h1>
            {getDemoModeIndicator() && (
              <span className={getDemoModeIndicator().className}>
                {getDemoModeIndicator().icon} {getDemoModeIndicator().text}
              </span>
            )}
          </div>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {isDemoMode() 
              ? "Gerencie seus registros de pacientes (nomes mascarados para demonstração)"
              : "Gerencie seus registros de pacientes"
            }
          </p>
        </div>
        <button 
          onClick={handleAddPatient}
          className="flex items-center justify-center px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium hover:bg-brand-800 w-full sm:w-auto"
        >
          <Plus size={16} className="mr-2" />
          <span className="sm:inline">Adicionar Paciente</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Pesquisar pacientes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 flex-1"
              >
                <option value="all">Todos os Status</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>

              {/* Only show insurance filter for admin roles */}
              {currentUser && ['admin', 'operations', 'supervisor', 'super_admin'].includes(currentUser.role) && (
                <select
                  value={filterInsurance}
                  onChange={(e) => setFilterInsurance(e.target.value)}
                  className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 flex-1"
                >
                  <option value="all">Todos os Seguros</option>
                  {insuranceCompanies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando pacientes...</div>
        ) : filteredPatients.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'Nenhum paciente encontrado para sua pesquisa' : 'Nenhum paciente disponível'}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Cadastro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fornecedor de Seguro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Terapeuta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPatients.map((patient) => (
                    <tr 
                      key={patient.id} 
                      className="hover:bg-gray-50 hover:shadow-sm cursor-pointer transition-all duration-200 group"
                      onClick={() => handleViewPatient(patient.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap relative">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-brand-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 group-hover:text-brand-600 transition-colors">
                              {maskPatientName(`${patient.firstName} ${patient.lastName}`, patient.id)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {patient.patientId} • {calculateAge(patient.dateOfBirth)} anos
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(patient.createdAt)}</div>
                        <div className="text-sm text-gray-500">
                          {new Date(patient.createdAt).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.insuranceCompany ? patient.insuranceCompany.name : 'Não Atribuído'}
                        </div>
                        {patient.insuranceId && (
                          <div className="text-sm text-gray-500">ID: {patient.insuranceId}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {patient.primaryTherapistUser ? 
                            `${patient.primaryTherapistUser.firstName} ${patient.primaryTherapistUser.lastName}` 
                            : 'Não Atribuído'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          patient.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {patient.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <div 
                  key={patient.id} 
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-all duration-200 group"
                  onClick={() => handleViewPatient(patient.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center flex-1">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-full bg-brand-100 flex items-center justify-center">
                          <User className="h-6 w-6 text-brand-600" />
                        </div>
                      </div>
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 group-hover:text-brand-600 transition-colors truncate">
                          {maskPatientName(`${patient.firstName} ${patient.lastName}`, patient.id)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {patient.patientId} • {calculateAge(patient.dateOfBirth)} anos
                        </div>
                        <div className="mt-1">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            patient.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {patient.status === 'active' ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-2">
                      <ChevronRight className="h-5 w-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">Data de Cadastro:</span>
                      <div className="text-gray-900">{formatDate(patient.createdAt)}</div>
                      <div className="text-gray-600 text-xs">
                        {new Date(patient.createdAt).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>

                    <div>
                      <span className="font-medium text-gray-500">Seguro:</span>
                      <div className="text-gray-900">
                        {patient.insuranceCompany ? patient.insuranceCompany.name : 'Não Atribuído'}
                      </div>
                      {patient.insuranceId && (
                        <div className="text-gray-600 text-xs">ID: {patient.insuranceId}</div>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <span className="font-medium text-gray-500">Terapeuta:</span>
                      <div className="text-gray-900">
                        {patient.primaryTherapistUser ?
                          `${patient.primaryTherapistUser.firstName} ${patient.primaryTherapistUser.lastName}`
                          : 'Não Atribuído'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este paciente? Esta ação não pode ser desfeita.
            </p>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 w-full sm:w-auto"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 w-full sm:w-auto"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="sr-only">Fechar</span>
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          <span className="block sm:inline">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="sr-only">Fechar</span>
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PatientList; 